import type { AxiosRequestConfig } from 'axios';
import { AxiosError, CanceledError } from 'axios';

import ApiCanceledError from 'lib/errors/ApiCanceledError';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiRequestError from 'lib/errors/ApiRequestError';
import ApiResponseError, { ServerError } from 'lib/errors/ApiResponseError';

interface ResponseDataError {
  errors: ServerError[];
}

/**
 * Takes an Axios error and rethrows it with our custom errors.
 *
 * This MUST be used when directly fetching with Axios because we want to
 * handle errors as `ApiNetworkError`s or `ApiResponseError`s.
 */
export default function handleAxiosError({
  error: unknownError,
  method,
  uri,
}: {
  error: unknown;
  method: AxiosRequestConfig['method'];
  uri: string;
}): never {
  if (!(unknownError instanceof AxiosError)) {
    throw unknownError;
  }

  const error: AxiosError<ResponseDataError> = unknownError;
  if (error instanceof CanceledError) {
    throw new ApiCanceledError({
      method,
      uri,
    });
  }
  if (error.response) {
    throw new ApiResponseError({
      method,
      status: error.response.status,
      uri,
      errors: error.response.data?.errors,
      code: error.code,
    });
  } else if (error.request || error.message === 'Network Error') {
    // No server response, i.e. network error
    throw new ApiNetworkError({
      method,
      uri,
      code: error.code,
    });
  } else {
    throw new ApiRequestError({ method, uri });
  }
}
